import type { AppNotification, NotificationRepository } from "../../../domain/notification/notification.js";

export class InMemoryNotificationRepository implements NotificationRepository {
  private notifications: Map<string, AppNotification> = new Map();
  private readonly storageKey = 'inspect_notifications';

  constructor() {
    this.load();
  }

  private load(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const data = localStorage.getItem(this.storageKey);
      if (data) {
        const parsed = JSON.parse(data) as [string, AppNotification][];
        this.notifications = new Map(parsed);
      }
    } catch (err) {
      console.warn("Failed to load notifications from localStorage", err);
    }
  }

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const data = JSON.stringify(Array.from(this.notifications.entries()));
      localStorage.setItem(this.storageKey, data);
    } catch (err) {
      console.warn("Failed to persist notifications", err);
    }
  }

  async save(notification: AppNotification): Promise<void> {
    this.notifications.set(notification.id, notification);
    this.persist();
  }

  async findById(id: string): Promise<AppNotification | null> {
    return this.notifications.get(id) || null;
  }

  async findByUserId(userId: string): Promise<AppNotification[]> {
    const list = Array.from(this.notifications.values())
      .filter(n => n.userId === userId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return list;
  }

  async getUnreadCount(userId: string): Promise<number> {
    const list = Array.from(this.notifications.values());
    return list.filter(n => n.userId === userId && !n.isRead).length;
  }

  async markAsRead(id: string): Promise<void> {
    const notif = this.notifications.get(id);
    if (notif && !notif.isRead) {
      notif.isRead = true;
      notif.readAt = new Date().toISOString();
      this.persist();
    }
  }

  async markAllAsRead(userId: string): Promise<void> {
    let changed = false;
    for (const notif of this.notifications.values()) {
      if (notif.userId === userId && !notif.isRead) {
        notif.isRead = true;
        notif.readAt = new Date().toISOString();
        changed = true;
      }
    }
    if (changed) this.persist();
  }

  async findByIdempotencyKey(key: string): Promise<AppNotification | null> {
    for (const notif of this.notifications.values()) {
      if (notif.idempotencyKey === key) return notif;
    }
    return null;
  }
}
